import React from 'react';
import { Lock, Shield, AlertCircle } from 'lucide-react';

const RequireAccess = ({ currentMember, permission, module }) => {
  // Check if user has access
  if (currentMember?.permissions?.[permission] || currentMember?.joined_as === 'owner' || currentMember?.is_admin) {
    return null; // User has access, don't render anything
  }

  // Map module names to icons and descriptions
  const moduleInfo = {
    Finance: {
      icon: '💰',
      description: 'financial data and reporting'
    },
    Vendor: {
      icon: '🏢',
      description: 'vendor management and relationships'
    },
    Contractor: {
      icon: '👷',
      description: 'contractor information and assignments'
    },
    Procurement: {
      icon: '📦',
      description: 'procurement processes and orders'
    },
    'Organization Team': {
      icon: '👥',
      description: 'team management and organizational structure'
    },
    Projects: {
      icon: '📊',
      description: 'project planning and execution'
    }
  };

  const currentModule = moduleInfo[module] || {
    icon: '🔒',
    description: 'this module'
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col  w-[84%] h-[90vh] items-center justify-center">
      <div className="w-[45vw] py-2">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-6 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-4">
              <Lock className="w-10 h-10 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Access Required</h1>
            <p className="text-slate-300 text-sm">
              You don't have permission to view this content
            </p>
          </div>

          {/* Content Section */}
          <div className="px-8 py-8">
            {/* Module Info */}
            <div className="flex items-start gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
              <div className="text-3xl">{currentModule.icon}</div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">{module}</h3>
                <p className="text-sm text-slate-600">
                  Access to {currentModule.description}
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold text-blue-600">1</span>
                </div>
                <div>
                  <p className="text-sm text-slate-700">
                    Contact your <span className="font-semibold text-slate-900">organization administrator</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold text-blue-600">2</span>
                </div>
                <div>
                  <p className="text-sm text-slate-700">
                    Request access to the <span className="font-semibold text-slate-900">{module}</span> module
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold text-blue-600">3</span>
                </div>
                <div>
                  <p className="text-sm text-slate-700">
                    Refresh this page once access has been granted
                  </p>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                Access permissions are managed by your organization administrators to ensure proper data security and compliance.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-6 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-medium">Protected by SiteMaster Security</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequireAccess;